// The chat bar: a launcher button and a panel docked to one side of the page (a bottom sheet on
// phones), inside a shadow root. It is the extension's side panel (sidepanel.js) moved into the
// page: one conversation per site per tab, kept in sessionStorage, so it follows the visitor from
// page to page and survives reloads.
import css from './styles.css';
import { compactHistory, continueTurn, repairHistory, runTurn } from '../lib/agent.js';
import { renderMarkdown } from '../lib/markdown.js';
import { listModels, synthesize, transcribe } from '../lib/openai.js';
import { STT_MODELS, TALK_KEYS, TTS_MODELS, VOICES, isTalkKey, talkKey, talkKeyNames, voiceSupported } from '../lib/speech.js';
import * as store from '../lib/store.js';
import { Recorder, Speaker, micErrorText, micPermission } from '../lib/voice.js';
import { ICONS, MARK } from './icons.js';

const SPEEDS = ['0.75', '1', '1.15', '1.3', '1.5', '1.75', '2'];

const TEMPLATE = `
<style>${css}</style>
<div class="vp" id="vp">
<div class="backdrop" id="backdrop"></div>
<button type="button" class="launcher" id="launcher" aria-label="Open the bc-agent chat" aria-expanded="false" aria-controls="panel">${MARK}<span class="badge" id="badge" hidden></span></button>
<section class="panel" id="panel" role="dialog" aria-label="bc-agent chat">
  <div class="resize" id="resize" title="Drag to resize"></div>
  <div class="grab" id="grab"></div>
  <header class="top" id="top">
    <div class="brand">
      <span class="mark">${MARK}</span>
      <div class="titles">
        <span class="name">bc-agent</span>
        <span class="site" id="site"><span class="dot"></span><span id="siteLabel"></span></span>
      </div>
    </div>
    <div class="actions">
      <button type="button" class="icon-btn" id="newChat" title="New chat" aria-label="New chat">${ICONS.plus}</button>
      <button type="button" class="icon-btn" id="openSettings" title="Settings" aria-label="Settings">${ICONS.settings}</button>
      <button type="button" class="icon-btn" id="close" title="Close" aria-label="Close the chat">${ICONS.close}</button>
    </div>
  </header>

  <main id="chatView" class="view">
    <div class="scroll" id="scroll"><div id="threadHost"></div></div>
    <div class="status" id="status" hidden><span class="spinner"></span><span id="statusText"></span></div>
    <div class="voice" id="voice" role="status" hidden>
      <span class="voice-icon"></span>
      <span class="voice-text" id="voiceText"></span>
      <button type="button" class="voice-action" id="voiceAction" hidden></button>
    </div>
    <form class="composer" id="composer">
      <textarea id="input" rows="1" placeholder="Ask bc-agent…" autocomplete="off" aria-label="Message"></textarea>
      <button type="button" class="mic" id="mic" title="Talk" aria-label="Talk" hidden>${ICONS.mic}<span class="spinner"></span></button>
      <button type="submit" class="send" id="send" title="Send (Enter)" aria-label="Send">${ICONS.send}</button>
      <button type="button" class="stop" id="stop" title="Stop" aria-label="Stop" hidden>${ICONS.stop}</button>
    </form>
  </main>

  <section id="settingsView" class="view settings" hidden>
    <div class="scroll">
      <h1>Settings</h1>
      <p class="muted">bc-agent runs in your browser with your own OpenAI API key. The key is kept in this browser, for this site only, and is sent only to the API address below.</p>
      <form id="settingsForm">
        <label class="field">
          <span>OpenAI API key</span>
          <div class="row">
            <input type="password" id="apiKey" placeholder="sk-…" autocomplete="off" spellcheck="false" />
            <button type="button" class="btn ghost" id="toggleKey">Show</button>
          </div>
          <small class="muted">Create one at <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">platform.openai.com/api-keys</a>. A project key with a monthly budget is safest.</small>
        </label>
        <label class="check">
          <input type="checkbox" id="rememberKey" />
          <span>
            <strong>Remember my key on this device</strong>
            <small class="muted">Off: the key is forgotten when you close this tab. Scripts on this site can read what it stores, so don't use a shared computer.</small>
          </span>
        </label>
        <label class="field">
          <span>Model</span>
          <div class="row">
            <input type="text" id="model" list="models" spellcheck="false" />
            <button type="button" class="btn ghost" id="loadModels">Check key</button>
          </div>
          <datalist id="models"></datalist>
          <small class="muted">Any chat model that supports tool calling. Bigger models do more and explain better; smaller ones are faster and cheaper.</small>
        </label>
        <label class="field">
          <span>Reasoning effort</span>
          <select id="reasoningEffort">
            <option value="">Model default</option>
            <option value="none">None</option>
            <option value="minimal">Minimal</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </label>
        <div id="voiceSection">
          <h2>Voice</h2>
          <label class="check" id="sttRow">
            <input type="checkbox" id="sttEnabled" />
            <span>
              <strong>Speech to text</strong>
              <small class="muted">Talk to bc-agent: click the mic next to the message box, or hold a shortcut. Your speech is sent to OpenAI for transcription.</small>
            </span>
          </label>
          <div class="voice-opts" id="sttOpts" hidden>
            <div class="mic-state" id="micState"></div>
            <label class="check">
              <input type="checkbox" id="sttHotkey" />
              <span>
                <strong>Hold a shortcut to talk</strong>
                <small class="muted">Works anywhere on this page while the chat is open. Release to finish, Esc to cancel.</small>
              </span>
            </label>
            <label class="field">
              <span>Shortcut</span>
              <select id="sttKey"></select>
              <small class="muted">Windows keeps Alt+Space for the window menu, so web pages never receive it there.</small>
            </label>
            <label class="field">
              <span>After you speak</span>
              <select id="sttAutoSend">
                <option value="1">Send the message automatically</option>
                <option value="0">Put the text in the message box to review</option>
              </select>
            </label>
            <div class="field-pair">
              <label class="field">
                <span>Transcription model</span>
                <input type="text" id="sttModel" list="sttModels" spellcheck="false" />
                <datalist id="sttModels"></datalist>
              </label>
              <label class="field">
                <span>Language</span>
                <input type="text" id="sttLanguage" placeholder="Auto-detect" spellcheck="false" />
              </label>
            </div>
            <small class="muted hint">Language is optional: a code such as <code>en</code>, or several (<code>en, fr</code>) for gpt-transcribe.</small>
          </div>
          <label class="check" id="ttsRow">
            <input type="checkbox" id="ttsEnabled" />
            <span>
              <strong>Text to speech</strong>
              <small class="muted">Read bc-agent's replies aloud in an AI-generated OpenAI voice. Esc or Stop silences it; talking interrupts it.</small>
            </span>
          </label>
          <div class="voice-opts" id="ttsOpts" hidden>
            <label class="field">
              <span>Read aloud</span>
              <select id="ttsWhen">
                <option value="always">Every reply</option>
                <option value="voice">Only replies to messages I spoke</option>
              </select>
            </label>
            <div class="field-pair">
              <label class="field">
                <span>Voice</span>
                <div class="row">
                  <select id="ttsVoice"></select>
                  <button type="button" class="btn ghost" id="previewVoice">Preview</button>
                </div>
              </label>
              <label class="field narrow">
                <span>Speed</span>
                <select id="ttsSpeed">${SPEEDS.map((s) => `<option value="${s}">${s}×</option>`).join('')}</select>
              </label>
            </div>
            <label class="field">
              <span>Speaking style</span>
              <input type="text" id="ttsStyle" placeholder="Optional, e.g. warm and upbeat, a little brisk" spellcheck="false" />
              <small class="muted">Instructions for the voice's tone and pace. gpt-4o-mini-tts only.</small>
            </label>
            <label class="field">
              <span>Speech model</span>
              <input type="text" id="ttsModel" list="ttsModels" spellcheck="false" />
              <datalist id="ttsModels"></datalist>
              <small class="muted">tts-1 and tts-1-hd have fewer voices and ignore the speaking style.</small>
            </label>
          </div>
        </div>
        <p class="muted" id="noVoice" hidden>This browser can't record audio, so speech to text is off.</p>
        <details class="advanced">
          <summary>Advanced</summary>
          <label class="field">
            <span>API base URL</span>
            <input type="url" id="baseUrl" spellcheck="false" />
            <small class="muted">Any OpenAI-compatible endpoint that allows requests from web pages.</small>
          </label>
          <label class="field">
            <span>Max tool steps per message</span>
            <input type="number" id="maxSteps" min="1" max="100" />
          </label>
        </details>
        <div class="form-msg" id="settingsMsg" role="status"></div>
        <div class="form-actions">
          <button type="button" class="btn ghost" id="closeSettings">Back to chat</button>
          <button type="submit" class="btn primary">Save</button>
        </div>
      </form>
      <p class="about muted" id="about"></p>
    </div>
  </section>
</section>
</div>`;

function el(tag, cls, text) {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** "ctrl+shift+y" → a KeyboardEvent matcher (or null for "off"). */
function parseHotkey(spec) {
  const parts = String(spec || '')
    .toLowerCase()
    .split('+')
    .map((s) => s.trim())
    .filter(Boolean);
  const key = parts.pop();
  if (!key || key === 'off' || key === 'none') return null;
  const mods = new Set(parts);
  return (e) =>
    e.ctrlKey === mods.has('ctrl') &&
    e.shiftKey === mods.has('shift') &&
    e.altKey === mods.has('alt') &&
    e.metaKey === mods.has('meta') &&
    (key.length === 1 ? e.code === `Key${key.toUpperCase()}` || e.code === `Digit${key}` : e.key.toLowerCase() === key);
}

/**
 * Mount the chat bar.
 * config: from the script tag (see index.js). entry: the registry row for this site.
 * loadSite(): the site's adapter (its chunk). loadScreenshot(): the screenshot module.
 * firewall: { host, handle } — the window-level key listener installed before the app's.
 */
export function mountWidget({ config, entry, loadSite, loadScreenshot, firewall }) {
  const hostEl = document.createElement('bc-agent');
  // inline !important: the page's own rules (body > *, *) can't move or hide the chat bar
  hostEl.style.cssText =
    'all:initial!important;display:block!important;position:fixed!important;top:0!important;left:0!important;width:0!important;height:0!important;overflow:visible!important;z-index:2147483000!important;';
  hostEl.setAttribute('side', config.side);
  hostEl.setAttribute('theme', config.theme);
  if (config.accent) hostEl.style.setProperty('--accent', config.accent);
  if (config.offsetX) hostEl.style.setProperty('--ox', config.offsetX);
  if (config.offsetY) hostEl.style.setProperty('--oy', config.offsetY);
  const root = hostEl.attachShadow({ mode: 'open' });
  root.innerHTML = TEMPLATE;
  const $ = (id) => root.getElementById(id);

  // the site's defaults for the visitor's settings (data-model, data-reasoning, data-max-steps)
  const siteDefaults = Object.fromEntries(
    Object.entries({ model: config.model, reasoningEffort: config.reasoningEffort, maxSteps: config.maxSteps }).filter(([, v]) => v),
  );
  const state = {
    site: null, // the adapter, once its chunk has loaded
    sitePromise: null,
    history: store.loadHistory(entry.id),
    running: false,
    controller: null,
    gen: 0, // bumped when a run is abandoned (back/forward cache), so it can't touch the UI later
    status: '',
    toolRows: new Map(),
    open: false,
    prefs: store.loadPrefs(siteDefaults),
    viaVoice: false,
    resumed: null, // the note from the page before, while a resumed turn runs
    width: 400,
  };
  // the visible width, not the layout viewport's (see fitViewport)
  const phone = () => (window.visualViewport ? window.visualViewport.width * window.visualViewport.scale : window.innerWidth) <= 640;
  const hotkey = config.hotkey ? parseHotkey(config.hotkey) : null;

  /* ------------------------------------------------------------------ */
  /* site and settings                                                   */
  /* ------------------------------------------------------------------ */

  function ensureSite() {
    state.sitePromise ??= loadSite()
      .then((site) => {
        if (!site) throw new Error(`bc-agent has no adapter for ${entry.name}.`);
        state.site = site;
        if (!state.running) rebuild(); // the empty state and tool labels come from the adapter
        updateComposer();
        return site;
      })
      .catch((e) => {
        state.sitePromise = null;
        throw new Error(`Couldn't load bc-agent for ${entry.name}: ${e.message ?? e}`);
      });
    return state.sitePromise;
  }

  /** What the API calls need: everything is the visitor's own settings, key included. */
  const settings = () => ({ ...state.prefs });
  const hasKey = () => !!state.prefs.apiKey;

  const voiceInAvailable = () => !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';
  const voiceOutAvailable = () => typeof Audio !== 'undefined';
  const voiceInOn = () => state.prefs.sttEnabled && voiceInAvailable();
  const voiceOutOn = () => state.prefs.ttsEnabled && voiceOutAvailable();
  /** Inside a tap or key press: let replies play later (iOS). Only when they will be read aloud. */
  const unlockAudio = () => voiceOutOn() && speaker.unlock();
  const screenshotsOn = () => config.screenshots;

  /* ------------------------------------------------------------------ */
  /* thread                                                              */
  /* ------------------------------------------------------------------ */

  const thread = el('div', 'thread');
  $('threadHost').appendChild(thread);

  function persist() {
    store.saveHistory(entry.id, compactHistory(state.history));
  }

  function rebuild() {
    thread.innerHTML = '';
    state.toolRows.clear();
    if (!state.history.length) {
      thread.appendChild(emptyState());
      return;
    }
    for (const m of state.history) {
      if (m._kind === 'error') appendError(m.content);
      else if (m.role === 'user' && !m._kind) appendUser(typeof m.content === 'string' ? m.content : '');
      else if (m.role === 'assistant') {
        if (m.content) appendAssistant(m.content);
        if (m.tool_calls) appendTools(m.tool_calls);
      } else if (m.role === 'tool') finishTool(m.tool_call_id, !/^(Error|Cancelled)/.test(m.content), m.content);
    }
    if (!state.running) for (const row of state.toolRows.values()) if (!row.done) setToolState(row, 'err', 'Interrupted');
    scrollDown(true);
  }

  function emptyState() {
    const site = state.site;
    const box = el('div', 'empty');
    if (!site) {
      const wait = el('div', 'loading');
      wait.appendChild(el('span', 'spinner'));
      box.appendChild(wait);
      return box;
    }
    const hero = el('div', 'hero');
    hero.innerHTML = MARK;
    box.appendChild(hero);
    box.appendChild(el('h2', null, `bc-agent for ${site.name}`));
    box.appendChild(el('p', null, site.intro ?? 'Tell me what to do and I’ll do it in the page. Every change is one undo step (Ctrl+Z).'));
    if (site.suggestions?.length) {
      const list = el('div', 'suggestions');
      for (const s of site.suggestions) {
        const b = el('button', 'suggestion', s);
        b.type = 'button';
        b.addEventListener('click', () => {
          $('input').value = s;
          autosize();
          $('input').focus();
        });
        list.appendChild(b);
      }
      box.appendChild(list);
    }
    if (!hasKey()) {
      const need = el('div', 'need-key');
      need.appendChild(el('p', null, 'bc-agent runs in your browser with your own OpenAI API key.'));
      const b = el('button', 'btn primary', 'Add your API key');
      b.type = 'button';
      b.addEventListener('click', () => showSettings());
      need.appendChild(b);
      box.appendChild(need);
    }
    box.appendChild(el('p', 'fine', 'Replies are AI-generated and can be wrong.'));
    return box;
  }

  /** The empty state depends on the settings (the key notice). */
  function rebuildIfEmpty() {
    if (!state.history.length && !state.running) rebuild();
  }

  const clearEmpty = () => thread.querySelector('.empty')?.remove();

  function scrollDown(force = false) {
    const s = $('scroll');
    if (force || s.scrollHeight - s.scrollTop - s.clientHeight < 140) s.scrollTop = s.scrollHeight;
  }

  function appendUser(text) {
    clearEmpty();
    thread.appendChild(el('div', 'msg-user', text));
    scrollDown(true);
  }

  function appendAssistant(text) {
    clearEmpty();
    const div = el('div', 'msg-assistant');
    div.innerHTML = renderMarkdown(text);
    thread.appendChild(div);
    scrollDown();
    return div;
  }

  function appendError(text) {
    clearEmpty();
    thread.appendChild(el('div', 'error-box', text));
    scrollDown(true);
  }

  function appendNotice(text) {
    thread.appendChild(el('div', 'notice', text));
    scrollDown();
  }

  function describeCall(name, args) {
    if (name === 'take_screenshot') return 'Look at the page';
    try {
      return state.site?.describe?.(name, args) ?? name;
    } catch {
      return name;
    }
  }

  function pretty(json) {
    try {
      return JSON.stringify(JSON.parse(json), null, 1);
    } catch {
      return String(json ?? '');
    }
  }

  function appendTools(calls) {
    clearEmpty();
    const box = el('div', 'tools');
    for (const call of calls) {
      let args = {};
      try {
        args = JSON.parse(call.function.arguments || '{}');
      } catch {
        /* shown raw below */
      }
      const row = el('details', 'tool');
      const summary = el('summary');
      const st = el('span', 'state');
      st.appendChild(el('span', 'spinner'));
      const label = el('span', 'label', describeCall(call.function.name, args));
      summary.append(st, label, el('span', 'chev', '›'));
      const pre = el('pre');
      pre.append(el('b', null, `${call.function.name}`), document.createTextNode(` ${pretty(call.function.arguments)}`));
      row.append(summary, pre);
      box.appendChild(row);
      state.toolRows.set(call.id, { row, box, state: st, pre, label, name: call.function.name, args, done: false });
    }
    thread.appendChild(box);
    scrollDown();
  }

  function setToolState(row, kind, resultText) {
    row.done = true;
    row.row.classList.remove('ok', 'err');
    row.row.classList.add(kind);
    row.state.textContent = kind === 'ok' ? '✓' : '✕';
    if (resultText !== undefined) {
      row.pre.appendChild(el('b', null, '\n\n→ '));
      row.pre.appendChild(document.createTextNode(pretty(resultText)));
    }
    if (kind === 'err') row.row.title = String(resultText ?? '').slice(0, 300);
  }

  function finishTool(id, ok, result) {
    const row = state.toolRows.get(id);
    if (!row || row.done) return;
    setToolState(row, ok ? 'ok' : 'err', result);
    let json = null;
    try {
      json = JSON.parse(result);
    } catch {
      /* not JSON */
    }
    // the model may refer to a track by id; relabel with the name the page reported
    if (ok && row.args?.track !== undefined && typeof json?.track === 'string') row.label.textContent = describeCall(row.name, { ...row.args, track: json.track });
    // a link the site wants the visitor to be able to open (a blocked pop-up, or to open again)
    if (ok && json?.link?.url && /^https?:\/\//.test(json.link.url)) {
      let links = row.box.nextElementSibling;
      if (!links?.classList.contains('links')) {
        links = el('div', 'tools links');
        row.box.after(links);
      }
      const a = el('a', 'link-chip');
      a.href = json.link.url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.append(document.createTextNode(`Open ${json.link.label || json.link.url}`));
      a.insertAdjacentHTML('beforeend', ICONS.external);
      links.appendChild(a);
    }
  }

  /* ------------------------------------------------------------------ */
  /* running the agent                                                   */
  /* ------------------------------------------------------------------ */

  function setStatus(text) {
    state.status = text || '';
    $('status').hidden = !state.status;
    $('statusText').textContent = state.status;
  }

  /** `viaVoice`: the message was spoken (for "only read replies to messages I spoke"). */
  async function send(text, { viaVoice = false } = {}) {
    if (state.running || !text.trim()) return;
    if (!hasKey()) {
      $('input').value = text; // keep it for after the key is in
      autosize();
      showSettings('Add your OpenAI API key to start.');
      return;
    }
    speaker.stop();
    try {
      await ensureSite();
    } catch (e) {
      appendError(e.message);
      return;
    }
    appendUser(text);
    await runAgent((opts) => runTurn({ ...opts, userText: text }), { viaVoice });
  }

  /** Finish a turn the page before started (it navigated or reloaded mid-turn). */
  async function resume(note) {
    state.running = true; // the tool rows still waiting are about to finish, not interrupted
    updateComposer();
    try {
      if (!hasKey()) throw new Error('The OpenAI API key is gone (it was kept for one tab only). Add it in Settings and ask again.');
      await ensureSite();
    } catch (e) {
      state.running = false;
      repairHistory(state.history);
      persist();
      rebuild();
      updateComposer();
      appendError(e.message);
      return;
    }
    rebuild();
    await runAgent((opts) => continueTurn(opts), { viaVoice: !!note.viaVoice, resumed: note });
  }

  async function runAgent(start, { viaVoice, resumed = null }) {
    const gen = ++state.gen;
    const live = () => gen === state.gen;
    state.running = true;
    state.controller = new AbortController();
    state.viaVoice = viaVoice;
    state.resumed = resumed;
    updateComposer();

    let stream = null;
    let raw = '';
    try {
      await start({
        settings: settings(),
        site: state.site,
        ctx: { resumed, navigate, openLink },
        history: state.history,
        signal: state.controller.signal,
        capture: screenshotsOn() ? capture : null,
        emit(type, data) {
          if (!live()) return;
          if (type === 'status') setStatus(data);
          else if (type === 'assistant_start') {
            raw = '';
            stream = null;
          } else if (type === 'text') {
            if (!stream) {
              stream = appendAssistant('');
              stream.classList.add('streaming');
              setStatus('');
            }
            raw += data;
            stream.innerHTML = renderMarkdown(raw);
            scrollDown();
          } else if (type === 'assistant_end') {
            if (stream) {
              stream.classList.remove('streaming');
              stream.innerHTML = renderMarkdown(data.content ?? raw);
            } else if (data.content) appendAssistant(data.content);
            stream = null;
            if (data.content && !state.controller?.signal.aborted) {
              readAloud(data.content, viaVoice);
              if (!state.open) markUnread();
            }
            if (data.tool_calls) {
              appendTools(data.tool_calls);
              setStatus('Working…');
            }
            persist();
          } else if (type === 'tool_start') setStatus(`${describeCall(data.name, data.args)}…`);
          else if (type === 'tool_end') {
            finishTool(data.id, data.ok, data.result);
            persist();
          }
        },
      });
    } catch (e) {
      if (!live()) return;
      stream?.classList.remove('streaming');
      if (e.name === 'AbortError') appendNotice('Stopped.');
      else {
        const msg = e.message ?? String(e);
        appendError(msg);
        state.history.push({ role: 'assistant', _kind: 'error', content: msg });
        if (!state.open) markUnread();
      }
      repairHistory(state.history);
      for (const row of state.toolRows.values()) if (!row.done) setToolState(row, 'err', 'Interrupted');
    } finally {
      if (live()) {
        state.running = false;
        state.controller = null;
        state.resumed = null;
        setStatus('');
        persist();
        updateComposer();
      }
    }
  }

  /**
   * Leave this page for `url` with the turn still running: save it, note how to carry on, and
   * go. The next page's chat bar picks the note up (resume) and runs the same tool call again,
   * which then finds itself already there. `key` names this navigation: one that doesn't land
   * where it should is not made twice.
   */
  async function navigate(url, { key } = {}) {
    const keys = state.resumed?.keys ?? [];
    if (key && keys.includes(key)) throw new Error(`The page didn't end up where expected (it is ${location.pathname}${location.search}).`);
    persist();
    store.saveOpen(state.open);
    store.saveResume(entry.id, { viaVoice: state.viaVoice, keys: key ? [...keys, key] : keys, open: state.open });
    setStatus('Opening the page…');
    location.assign(url);
    await sleep(20000);
    // still here: the navigation was cancelled (e.g. the page asked to stay)
    store.takeResume(entry.id);
    throw new Error("The page didn't navigate. Try again.");
  }

  /** Open a link in a new tab. A blocked pop-up still leaves a button in the chat (the result's `link`). */
  function openLink(url, label, extra = '') {
    let opened = false;
    try {
      const w = window.open(url, '_blank');
      if (w) {
        opened = true;
        try {
          w.opener = null;
        } catch {
          /* cross-origin already */
        }
      }
    } catch {
      /* blocked */
    }
    const tail = extra ? ` ${extra}` : '';
    return opened
      ? { opened: label, link: { url, label }, note: `Opened in a new tab.${tail}` }
      : { opened: false, link: { url, label }, note: `The browser blocked the new tab, so the chat shows a button the visitor can click to open it.${tail}` };
  }

  async function capture() {
    const mod = await loadScreenshot();
    return mod.captureViewport({ exclude: hostEl });
  }

  /* ------------------------------------------------------------------ */
  /* open, close, unread                                                 */
  /* ------------------------------------------------------------------ */

  function setOpen(open, { focus = true } = {}) {
    if (state.open === open) return;
    state.open = open;
    hostEl.toggleAttribute('open', open);
    $('launcher').setAttribute('aria-expanded', String(open));
    store.saveOpen(open);
    if (open) {
      applyTheme();
      $('badge').hidden = true;
      renderLauncher();
      ensureSite().catch((e) => {
        if (!state.history.length) thread.innerHTML = '';
        appendError(e.message);
      });
      fitViewport();
      if (focus && !phone()) setTimeout(() => $('input').focus({ preventScroll: true }), 60);
    } else {
      if (recording()) void cancelTalking();
      speaker.stop();
      if (focus && root.activeElement) $('launcher').focus({ preventScroll: true });
    }
  }

  const toggle = () => setOpen(!state.open);

  function markUnread() {
    $('badge').hidden = false;
  }

  function renderLauncher() {
    $('launcher').classList.toggle('busy', state.running && !state.open);
  }

  /* ------------------------------------------------------------------ */
  /* voice: speech to text and text to speech                            */
  /* ------------------------------------------------------------------ */

  const MIN_RECORDING_MS = 350;
  const MAX_RECORDING_MS = 120000;
  const SILENT_PEAK = 0.001; // peak sample; a muted microphone sends zeros, a live one in a quiet room is well above this

  // state: 'idle' | 'starting' | 'recording' | 'transcribing'
  // source: what started the recording: 'button' | 'key' (in the chat) | 'page' (on the site)
  const voice = { state: 'idle', source: null, stopWhenStarted: false, cancelled: false, draft: false, maxTimer: null };
  const recorder = new Recorder();
  const speaker = new Speaker({ synthesize, onState: onSpeakerState });
  const recording = () => voice.state === 'starting' || voice.state === 'recording';
  const held = new Set(); // keys of the talk chord still down

  let voiceBarTimer = null;
  const dismiss = { label: 'Dismiss', run: () => showVoiceBar(null) };

  /** The strip above the message box: kind is 'listen' | 'busy' | 'speak' | 'info' | 'err', or null to hide. */
  function showVoiceBar(kind, text = '', action = null) {
    clearTimeout(voiceBarTimer);
    const bar = $('voice');
    bar.hidden = !kind;
    if (!kind) return;
    bar.className = `voice ${kind}`;
    $('voiceText').textContent = text;
    const btn = $('voiceAction');
    btn.hidden = !action;
    if (action) {
      btn.textContent = action.label;
      btn.onclick = action.run;
    }
    if (kind === 'info') voiceBarTimer = setTimeout(() => showVoiceBar(null), 4500);
  }

  const keyLabel = () => talkKey(state.prefs.sttKey).label;
  const talkKeyOn = () => state.open && voiceInOn() && state.prefs.sttHotkey && $('settingsView').hidden;

  function renderMic() {
    const mic = $('mic');
    mic.hidden = !voiceInOn();
    mic.classList.toggle('starting', voice.state === 'starting');
    mic.classList.toggle('recording', voice.state === 'recording');
    mic.classList.toggle('busy', voice.state === 'transcribing');
    mic.setAttribute('aria-pressed', String(recording()));
    if (!recording()) mic.style.setProperty('--level', 0);
    mic.title = recording()
      ? 'Finish and transcribe'
      : voice.state === 'transcribing'
        ? 'Transcribing…'
        : state.prefs.sttHotkey && !phone()
          ? `Talk: click to start and stop, or hold ${keyLabel()}`
          : 'Talk: tap to start and stop';
  }

  function listeningText(ms) {
    const s = Math.floor(ms / 1000);
    const time = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
    const finish = voice.source === 'button' ? `${phone() ? 'tap' : 'click'} the mic to finish` : `release ${keyLabel()} to finish`;
    return `Listening ${time} · ${finish}${phone() ? '' : ', Esc to cancel'}`;
  }

  function renderMicState(st) {
    const box = $('micState');
    box.innerHTML = '';
    box.className = `mic-state ${st === 'granted' ? 'ok' : st === 'denied' ? 'err' : ''}`;
    box.appendChild(el('span', null, st === 'granted' ? '✓ Microphone allowed' : st === 'denied' ? 'Microphone blocked for this site: allow it from the address bar' : 'Microphone not allowed yet'));
    if (st === 'prompt') {
      const b = el('button', 'btn ghost', 'Allow microphone');
      b.type = 'button';
      b.addEventListener('click', () => void ensureMic());
      box.appendChild(b);
    }
  }

  /** Ask for the microphone (the browser shows its own prompt for this site). */
  async function ensureMic() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      for (const t of stream.getTracks()) t.stop();
    } catch (e) {
      settingsMsg(micErrorText(e), 'err');
    }
    renderMicState(await micPermission());
  }

  async function watchMic() {
    try {
      const status = await navigator.permissions.query({ name: 'microphone' });
      renderMicState(status.state);
      status.addEventListener('change', () => renderMicState(status.state));
    } catch {
      renderMicState('prompt');
    }
  }

  function idle() {
    clearTimeout(voice.maxTimer);
    voice.state = 'idle';
    voice.source = null;
    renderMic();
  }

  async function startTalking(source) {
    if (voice.state !== 'idle' || !voiceInOn()) return;
    if (!hasKey()) return showSettings('Add your OpenAI API key to use voice.');
    if (!$('settingsView').hidden) showChat();
    speaker.stop();
    unlockAudio();
    Object.assign(voice, { state: 'starting', source, stopWhenStarted: false, cancelled: false });
    renderMic();
    showVoiceBar('busy', 'Starting the microphone…');
    try {
      await recorder.start({
        onLevel(rms, ms) {
          if (voice.state !== 'recording') return;
          $('mic').style.setProperty('--level', Math.min(1, rms * 6).toFixed(2));
          $('voiceText').textContent = listeningText(ms);
        },
      });
    } catch (e) {
      idle();
      return showVoiceBar('err', micErrorText(e), dismiss);
    }
    if (voice.cancelled) {
      await recorder.cancel();
      return idle();
    }
    voice.state = 'recording';
    renderMic();
    showVoiceBar('listen', listeningText(0));
    voice.maxTimer = setTimeout(() => void stopTalking(), MAX_RECORDING_MS);
    if (voice.stopWhenStarted) void stopTalking();
  }

  async function stopTalking() {
    if (voice.state === 'starting') voice.stopWhenStarted = true;
    if (voice.state !== 'recording') return;
    const source = voice.source;
    clearTimeout(voice.maxTimer);
    voice.state = 'transcribing';
    renderMic();
    const { blob, filename, ms, peak } = await recorder.stop();
    if (ms < MIN_RECORDING_MS) {
      idle();
      return showVoiceBar('info', source === 'button' ? 'That was too short. Tap the mic, talk, then tap again.' : `Keep holding ${keyLabel()} while you talk.`);
    }
    if (peak !== null && peak < SILENT_PEAK) {
      idle();
      return showVoiceBar('err', 'No sound came through. Is the microphone muted?', dismiss);
    }
    showVoiceBar('busy', 'Transcribing…');
    let text;
    try {
      text = await transcribe({ settings: settings(), audio: blob, filename, keywords: [entry.name] });
    } catch (e) {
      idle();
      return showVoiceBar('err', `Transcription failed: ${e.message ?? e}`, dismiss);
    }
    idle();
    if (!text) return showVoiceBar('info', 'Didn’t catch that. Try again.');
    deliverTranscript(text);
  }

  async function cancelTalking() {
    if (voice.state === 'starting') voice.cancelled = true;
    else if (voice.state === 'recording') {
      await recorder.cancel();
      idle();
    } else return;
    showVoiceBar('info', 'Cancelled.');
  }

  /** Send the transcript, or put it in the message box (review mode, or while the agent is busy). */
  function deliverTranscript(text) {
    const input = $('input');
    const full = [input.value.trim(), text].filter(Boolean).join(' ');
    if (state.prefs.sttAutoSend && !state.running) {
      input.value = '';
      autosize();
      voice.draft = false;
      showVoiceBar(null);
      void send(full, { viaVoice: true });
      return;
    }
    input.value = full;
    autosize();
    voice.draft = true;
    if (!phone()) input.focus();
    input.setSelectionRange(full.length, full.length);
    showVoiceBar('info', state.running && state.prefs.sttAutoSend ? 'bc-agent is still working. Press Enter to send this when it’s done.' : 'Check the text, then press Enter to send.');
  }

  function readAloud(text, viaVoice) {
    if (!voiceOutOn() || (state.prefs.ttsWhen === 'voice' && !viaVoice)) return;
    if (recording()) return;
    speaker.speak(text, settings());
  }

  function onSpeakerState({ speaking, error, blocked }) {
    $('previewVoice').textContent = speaker.speaking ? 'Stop' : 'Preview';
    const stop = { label: 'Stop', run: () => speaker.stop() };
    if (speaking === true) showVoiceBar('speak', 'Speaking…', stop);
    else if (speaking === false && $('voice').classList.contains('speak')) showVoiceBar(null);
    if (blocked) {
      showVoiceBar('speak', 'Your browser wants a tap before bc-agent speaks.', {
        label: 'Play',
        run: () => {
          blocked();
          showVoiceBar('speak', 'Speaking…', stop);
        },
      });
    }
    if (error) {
      const msg = `Couldn’t read the reply aloud: ${error.message ?? error}`;
      if (!$('settingsView').hidden) settingsMsg(msg, 'err');
      else showVoiceBar('err', msg, dismiss);
    }
  }

  function applyVoiceSettings() {
    if (!voiceOutOn()) speaker.stop();
    if (!voiceInOn() && recording()) void cancelTalking();
    renderMic();
  }

  /* ------------------------------------------------------------------ */
  /* keyboard (called from the window-level firewall in index.js)        */
  /* ------------------------------------------------------------------ */

  /**
   * Every keydown/keyup/keypress on the page comes here first. Returns true to swallow it.
   * Keys typed inside the chat bar never reach the page either way (the firewall stops them),
   * so the app's shortcuts (Space to play, Delete, Ctrl+Z…) don't fire while the visitor types.
   */
  function handleKey(e, inside) {
    const keyName = e.code === 'Space' ? 'Space' : e.key;
    if (e.type === 'keyup') {
      if (!held.delete(keyName)) return false;
      e.preventDefault();
      if (voice.source === 'key' || voice.source === 'page') void stopTalking();
      return true;
    }
    if (e.type !== 'keydown') return false;
    if (hotkey?.(e)) {
      e.preventDefault();
      toggle();
      return true;
    }
    if (isTalkKey(e, state.prefs.sttKey) && talkKeyOn()) {
      e.preventDefault();
      held.clear();
      for (const k of talkKeyNames(state.prefs.sttKey)) held.add(k);
      if (!e.repeat) void startTalking(inside ? 'key' : 'page');
      return true;
    }
    if (held.has('Space') && e.code === 'Space') {
      e.preventDefault();
      return true;
    }
    if (e.key === 'Escape' && recording()) {
      e.preventDefault();
      void cancelTalking();
      return true;
    }
    if (e.key === 'Escape' && speaker.speaking && (inside || state.open)) {
      e.preventDefault();
      speaker.stop();
      return true;
    }
    if (!inside) return false;
    const target = e.composedPath()[0];
    if (target === $('input') && e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      $('composer').requestSubmit();
    } else if (e.key === 'Escape' && state.open) {
      e.preventDefault();
      if (!$('settingsView').hidden) showChat();
      else setOpen(false);
    }
    return false;
  }

  function releaseKeys() {
    held.clear();
    if (voice.source === 'key' || voice.source === 'page') void stopTalking();
  }

  /* ------------------------------------------------------------------ */
  /* composer                                                            */
  /* ------------------------------------------------------------------ */

  function autosize() {
    const t = $('input');
    t.style.height = 'auto';
    t.style.height = Math.min(180, t.scrollHeight) + 'px';
  }

  function updateComposer() {
    const running = state.running;
    $('input').placeholder = state.site?.placeholder ?? `Ask bc-agent to do something in ${entry.name}…`;
    $('send').hidden = running;
    $('stop').hidden = !running;
    $('site').classList.toggle('on', !!state.site);
    renderMic();
    renderLauncher();
  }

  /* ------------------------------------------------------------------ */
  /* settings view                                                       */
  /* ------------------------------------------------------------------ */

  function fillVoices(chosen) {
    const model = $('ttsModel').value.trim() || store.DEFAULTS.ttsModel;
    const sel = $('ttsVoice');
    sel.innerHTML = '';
    for (const v of VOICES) {
      const o = el('option', null, `${v.id[0].toUpperCase()}${v.id.slice(1)}`);
      o.value = v.id;
      o.disabled = !voiceSupported(model, v.id);
      sel.appendChild(o);
    }
    sel.value = voiceSupported(model, chosen) ? chosen : 'coral';
  }

  function updateVoiceOpts() {
    $('sttOpts').hidden = !$('sttEnabled').checked;
    $('ttsOpts').hidden = !$('ttsEnabled').checked;
  }

  function renderVoiceAvailability() {
    const inOk = voiceInAvailable();
    const outOk = voiceOutAvailable();
    $('sttRow').hidden = !inOk;
    if (!inOk) $('sttOpts').hidden = true;
    $('ttsRow').hidden = !outOk;
    if (!outOk) $('ttsOpts').hidden = true;
    $('voiceSection').hidden = !inOk && !outOk;
    $('noVoice').hidden = inOk;
    if (phone()) $('sttKey').closest('.field').hidden = true;
  }

  function fillSettings() {
    const p = state.prefs;
    $('apiKey').value = p.apiKey;
    $('rememberKey').checked = p.rememberKey;
    $('model').value = p.model;
    $('reasoningEffort').value = p.reasoningEffort;
    $('baseUrl').value = p.baseUrl;
    $('maxSteps').value = p.maxSteps;
    $('sttModel').value = p.sttModel;
    $('ttsModel').value = p.ttsModel;
    $('sttEnabled').checked = p.sttEnabled;
    $('sttHotkey').checked = p.sttHotkey;
    $('sttKey').value = TALK_KEYS[p.sttKey] ? p.sttKey : store.DEFAULTS.sttKey;
    $('sttAutoSend').value = p.sttAutoSend ? '1' : '0';
    $('sttLanguage').value = p.sttLanguage;
    $('ttsEnabled').checked = p.ttsEnabled;
    $('ttsWhen').value = p.ttsWhen;
    fillVoices(p.ttsVoice);
    $('ttsSpeed').value = SPEEDS.includes(String(p.ttsSpeed)) ? String(p.ttsSpeed) : '1';
    $('ttsStyle').value = p.ttsStyle;
    updateVoiceOpts();
    renderVoiceAvailability();
  }

  function formPrefs() {
    return {
      apiKey: $('apiKey').value,
      rememberKey: $('rememberKey').checked,
      model: $('model').value,
      reasoningEffort: $('reasoningEffort').value,
      baseUrl: $('baseUrl').value,
      maxSteps: Number($('maxSteps').value) || store.DEFAULTS.maxSteps,
      sttModel: $('sttModel').value,
      ttsModel: $('ttsModel').value,
      sttEnabled: $('sttEnabled').checked,
      sttHotkey: $('sttHotkey').checked,
      sttKey: $('sttKey').value,
      sttAutoSend: $('sttAutoSend').value === '1',
      sttLanguage: $('sttLanguage').value,
      ttsEnabled: $('ttsEnabled').checked,
      ttsWhen: $('ttsWhen').value,
      ttsVoice: $('ttsVoice').value,
      ttsSpeed: Number($('ttsSpeed').value) || 1,
      ttsStyle: $('ttsStyle').value,
    };
  }

  function settingsMsg(text, kind = '') {
    const m = $('settingsMsg');
    m.textContent = text;
    m.className = `form-msg ${kind}`;
  }

  /** `message`: why the visitor is here (shown as an error, e.g. no key yet). */
  function showSettings(message) {
    fillSettings();
    settingsMsg(message ?? '', message ? 'err' : '');
    $('chatView').hidden = true;
    $('settingsView').hidden = false;
    if (message && !phone()) setTimeout(() => $('apiKey').focus({ preventScroll: true }), 60);
  }

  function showChat() {
    $('settingsView').hidden = true;
    $('chatView').hidden = false;
    if (!phone()) $('input').focus({ preventScroll: true });
  }

  /* ------------------------------------------------------------------ */
  /* layout: width, phone sheet, keyboard                                */
  /* ------------------------------------------------------------------ */

  function setWidth(w) {
    state.width = Math.round(Math.max(320, Math.min(w, Math.min(760, window.innerWidth - 80))));
    hostEl.style.setProperty('--w', `${state.width}px`);
  }

  function wireResize() {
    const handle = $('resize');
    let id = null;
    handle.addEventListener('pointerdown', (e) => {
      if (e.button !== 0) return;
      id = e.pointerId;
      handle.setPointerCapture(id);
      hostEl.setAttribute('dragging', '');
      e.preventDefault();
    });
    handle.addEventListener('pointermove', (e) => {
      if (e.pointerId !== id) return;
      setWidth(config.side === 'left' ? e.clientX - 12 : window.innerWidth - e.clientX - 12);
    });
    const end = (e) => {
      if (e.pointerId !== id) return;
      id = null;
      hostEl.removeAttribute('dragging');
      store.saveWidth(state.width);
    };
    handle.addEventListener('pointerup', end);
    handle.addEventListener('pointercancel', end);
    handle.addEventListener('dblclick', () => {
      setWidth(400);
      store.saveWidth(400);
    });
  }

  /** On a phone, pull the sheet down by its handle or header to close it. */
  function wireSheetDrag() {
    let startY = null;
    let delta = 0;
    let id = null;
    const down = (e) => {
      if (!phone() || !e.isPrimary || e.composedPath().some((n) => n.tagName === 'BUTTON')) return;
      startY = e.clientY;
      delta = 0;
      id = e.pointerId;
      e.currentTarget.setPointerCapture(id);
      hostEl.setAttribute('dragging', '');
    };
    const move = (e) => {
      if (startY === null || e.pointerId !== id) return;
      delta = Math.max(0, e.clientY - startY);
      hostEl.style.setProperty('--pull', `${delta}px`);
    };
    const up = (e) => {
      if (startY === null || e.pointerId !== id) return;
      startY = null;
      hostEl.removeAttribute('dragging');
      hostEl.style.removeProperty('--pull');
      if (delta > 90) setOpen(false);
    };
    for (const node of [$('grab'), $('top')]) {
      node.addEventListener('pointerdown', down);
      node.addEventListener('pointermove', move);
      node.addEventListener('pointerup', up);
      node.addEventListener('pointercancel', up);
    }
  }

  /**
   * Keep .vp over the visual viewport: the part of the page on screen. position: fixed follows
   * the layout viewport, which on a phone is wider than the screen when the page is (MIDI on a
   * phone is 1432px wide), and doesn't shrink for the on-screen keyboard. Pinch zoom is undone
   * with a scale, so the chat bar stays the same size on screen.
   */
  const tallest = new Map(); // visible height per width, to tell when the keyboard is up
  let fitQueued = false;
  function fitViewport() {
    const vv = window.visualViewport;
    if (!vv) return;
    const s = vv.scale || 1;
    const vp = $('vp');
    vp.style.left = `${vv.offsetLeft}px`;
    vp.style.top = `${vv.offsetTop}px`;
    vp.style.width = `${vv.width * s}px`;
    vp.style.height = `${vv.height * s}px`;
    vp.style.transform = Math.abs(s - 1) < 0.001 ? '' : `scale(${1 / s})`;
    const w = Math.round(vv.width * s);
    const h = vv.height * s;
    tallest.set(w, Math.max(tallest.get(w) ?? 0, h));
    const kb = phone() && tallest.get(w) - h > 120;
    if (kb !== hostEl.hasAttribute('kb')) {
      hostEl.toggleAttribute('kb', kb);
      if (kb && state.open) scrollDown(true);
    }
  }
  const queueFit = () => {
    if (fitQueued) return;
    fitQueued = true;
    requestAnimationFrame(() => {
      fitQueued = false;
      fitViewport();
    });
  };

  /** theme="auto": match the page under the chat bar (a dark app gets a dark panel), else the system setting. */
  function applyTheme() {
    if (config.theme !== 'auto') return hostEl.setAttribute('theme', config.theme);
    const rgb = (c) => {
      const m = /^rgba?\(([^)]+)\)$/.exec(c || '');
      if (!m) return null;
      const [r, g, b, a = 1] = m[1].split(/[\s,/]+/).filter(Boolean).map(Number);
      return a < 0.5 ? null : [r, g, b];
    };
    let dark = null;
    try {
      const x = config.side === 'left' ? 40 : window.innerWidth - 40;
      const hit = document.elementsFromPoint(Math.max(0, x), window.innerHeight / 2).find((n) => n !== hostEl);
      for (let n = hit ?? document.body; n && n.nodeType === 1; n = n.parentElement) {
        const c = rgb(getComputedStyle(n).backgroundColor);
        if (c) {
          dark = (0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2]) / 255 < 0.5;
          break;
        }
      }
    } catch {
      /* the system setting decides */
    }
    hostEl.setAttribute('theme', dark === null ? 'auto' : dark ? 'dark' : 'light');
  }

  /* ------------------------------------------------------------------ */
  /* wiring                                                              */
  /* ------------------------------------------------------------------ */

  $('siteLabel').textContent = entry.name;
  $('about').textContent = `bc-agent is an AI assistant for ${entry.name}. It runs entirely in your browser: what you type or say, and what is on this page, goes straight from your browser to OpenAI (or the API address above) with your key. The conversation is kept in this tab only.`;
  const savedWidth = store.loadWidth();
  setWidth(typeof savedWidth === 'number' ? savedWidth : config.width || 400);

  // Pointer, wheel, clipboard and input events in the chat bar stay in the chat bar, so the
  // app's own document listeners (click-away, wheel zoom, paste…) never see them.
  for (const t of ['pointerdown', 'mousedown', 'click', 'dblclick', 'contextmenu', 'wheel', 'touchstart', 'touchmove', 'touchend', 'focusin', 'focusout', 'input', 'change', 'beforeinput', 'paste', 'copy', 'cut', 'selectstart']) {
    root.addEventListener(t, (e) => e.stopPropagation(), { passive: true });
  }

  $('launcher').addEventListener('click', () => {
    unlockAudio();
    toggle();
  });
  $('launcher').addEventListener('pointerenter', () => void ensureSite().catch(() => {}), { once: true });
  $('close').addEventListener('click', () => setOpen(false));
  $('backdrop').addEventListener('click', () => setOpen(false));

  $('composer').addEventListener('submit', (e) => {
    e.preventDefault();
    const text = $('input').value;
    if (!text.trim() || state.running) return; // still working: the text stays in the box
    unlockAudio();
    $('input').value = '';
    autosize();
    void send(text, { viaVoice: voice.draft });
    voice.draft = false;
  });
  $('input').addEventListener('input', () => {
    autosize();
    if (!$('input').value.trim()) voice.draft = false;
  });
  $('stop').addEventListener('click', () => {
    state.controller?.abort();
    speaker.stop();
  });
  $('newChat').addEventListener('click', () => {
    if (state.running) {
      state.controller?.abort();
      state.gen++; // the stopped run must not write into the new chat
      state.running = false;
      state.controller = null;
      setStatus('');
    }
    speaker.stop();
    state.history = [];
    persist();
    rebuild();
    updateComposer();
    showChat();
  });
  $('openSettings').addEventListener('click', () => ($('settingsView').hidden ? showSettings() : showChat()));
  $('closeSettings').addEventListener('click', showChat);

  // the mic: tap to start and tap again to finish, or hold it while talking
  let pressedAt = 0;
  $('mic').addEventListener('pointerdown', (e) => {
    if (e.button !== 0) return;
    e.preventDefault(); // keep the caret in the message box
    if (voice.state === 'idle') {
      pressedAt = performance.now();
      void startTalking('button');
    } else if (voice.source === 'button') void stopTalking();
  });
  window.addEventListener('pointerup', () => {
    if (pressedAt && performance.now() - pressedAt > 450 && voice.source === 'button') void stopTalking();
    pressedAt = 0;
  });
  $('mic').addEventListener('click', (e) => {
    if (e.detail !== 0) return; // pointer clicks are handled on pointerdown; this is Enter/Space on the focused button
    if (voice.state === 'idle') void startTalking('button');
    else if (voice.source === 'button') void stopTalking();
  });

  // settings form
  $('toggleKey').addEventListener('click', () => {
    const k = $('apiKey');
    k.type = k.type === 'password' ? 'text' : 'password';
    $('toggleKey').textContent = k.type === 'password' ? 'Show' : 'Hide';
  });
  $('loadModels').addEventListener('click', async () => {
    const form = formPrefs();
    if (!form.apiKey.trim()) return settingsMsg('Add your OpenAI API key first.', 'err');
    settingsMsg('Checking…');
    try {
      const all = await listModels({ apiKey: form.apiKey.trim(), baseUrl: (form.baseUrl.trim() || store.DEFAULTS.baseUrl).replace(/\/+$/, '') });
      const chatty = all.filter((id) => /^(gpt-|o\d|chatgpt)/.test(id) && !/(audio|realtime|transcribe|tts|image|embedding|search|moderation)/.test(id));
      const list = chatty.length ? chatty : all;
      $('models').innerHTML = '';
      for (const id of list) $('models').appendChild(Object.assign(document.createElement('option'), { value: id }));
      settingsMsg(`Key works · ${list.length} model${list.length === 1 ? '' : 's'} available (pick one from the Model list).`, 'ok');
    } catch (e) {
      settingsMsg(e.message ?? String(e), 'err');
    }
  });
  for (const m of STT_MODELS) $('sttModels').appendChild(Object.assign(document.createElement('option'), { value: m }));
  for (const m of TTS_MODELS) $('ttsModels').appendChild(Object.assign(document.createElement('option'), { value: m }));
  $('ttsModel').addEventListener('input', () => fillVoices($('ttsVoice').value));
  for (const [id, k] of Object.entries(TALK_KEYS)) {
    const o = el('option', null, id === 'alt+space' ? `${k.label} (Mac only)` : k.label);
    o.value = id;
    $('sttKey').appendChild(o);
  }
  $('sttEnabled').addEventListener('change', (e) => {
    updateVoiceOpts();
    if (e.target.checked) void ensureMic();
  });
  $('ttsEnabled').addEventListener('change', updateVoiceOpts);
  $('previewVoice').addEventListener('click', async () => {
    if (speaker.speaking) return speaker.stop();
    speaker.unlock();
    const preview = { ...settings(), ...formPrefs() };
    if (!preview.apiKey.trim()) return settingsMsg('Add your OpenAI API key first.', 'err');
    preview.ttsModel = preview.ttsModel.trim() || store.DEFAULTS.ttsModel;
    preview.baseUrl = (preview.baseUrl.trim() || store.DEFAULTS.baseUrl).replace(/\/+$/, '');
    settingsMsg('');
    speaker.speak('Hi, I’m bc-agent. This is how I’ll sound when I read my replies to you.', preview);
  });
  $('settingsForm').addEventListener('submit', (e) => {
    e.preventDefault();
    state.prefs = store.savePrefs(formPrefs(), siteDefaults);
    fillSettings();
    applyVoiceSettings();
    rebuildIfEmpty();
    settingsMsg(hasKey() ? 'Saved.' : 'Saved. Add an API key to start chatting.', hasKey() ? 'ok' : 'err');
  });

  wireResize();
  wireSheetDrag();
  window.visualViewport?.addEventListener('resize', queueFit);
  window.visualViewport?.addEventListener('scroll', queueFit);
  window.addEventListener('resize', queueFit);
  matchMedia('(prefers-color-scheme: dark)').addEventListener?.('change', applyTheme);
  window.addEventListener('blur', releaseKeys);
  document.addEventListener('visibilitychange', () => document.hidden && releaseKeys());
  // back/forward cache: a page left mid-turn comes back frozen. The next page has carried the
  // turn on since, so take the conversation as it now stands.
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    state.gen++;
    state.running = false;
    state.controller = null;
    state.resumed = null;
    store.takeResume(entry.id);
    state.history = store.loadHistory(entry.id);
    repairHistory(state.history);
    setStatus('');
    rebuild();
    updateComposer();
  });

  firewall.host = hostEl;
  firewall.handle = handleKey;
  document.documentElement.appendChild(hostEl);
  void watchMic();
  fitViewport();
  applyTheme();
  // the app may paint its background after this script runs
  window.addEventListener('load', () => !state.open && applyTheme(), { once: true });

  // start: carry on a turn the previous page began, or restore the conversation as it was
  const note = store.takeResume(entry.id);
  const wasOpen = store.loadOpen();
  state.running = !!note;
  rebuild();
  updateComposer();
  if (note) {
    setOpen(note.open !== false, { focus: false });
    void resume(note);
  } else {
    if (repairHistory(state.history)) {
      persist();
      rebuild();
    }
    if (wasOpen ?? config.open) setOpen(true, { focus: false });
  }

  return {
    open: () => setOpen(true),
    close: () => setOpen(false),
    toggle,
    send: (text) => {
      setOpen(true, { focus: false });
      return send(String(text ?? ''));
    },
  };
}
